import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Laptop, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import styles from './PricingPage.module.css';

export function PricingPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async (tier: 'single' | 'family') => {
    if (!user) {
      navigate(`/login?redirect=/pricing&tier=${tier}`);
      return;
    }

    try {
      setLoadingTier(tier);
      setError(null);
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tier,
          uid: user.uid,
          email: user.email
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Payment service unavailable. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="app">
      <Header />
      <main className={styles.pricingPage}>
        <div className="wrap">
          <header className={styles.header}>
            <h1>Get Your Full PC Health Report</h1>
            <p>One-time payment. No subscriptions. Credits never expire.</p>
            
            {error && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                {error}
              </div>
            )}
          </header>

          <div className={styles.grid}>
            {/* Tier 1: Single Scan */}
            <div className={`${styles.card} ${loadingTier === 'single' ? styles.loading : ''}`}>
              <div className={styles.cardTop}>
                <div className={styles.icon}><Laptop size={24} /></div>
                <h3>Single Scan</h3>
                <div className={styles.price}>
                  <span className={styles.currency}>£</span>
                  <span className={styles.amount}>4.99</span>
                  <span className={styles.period}>one-time</span>
                </div>
              </div>
              <ul className={styles.features}>
                <li><Check size={16} /> 1 PC diagnostic scan</li>
                <li><Check size={16} /> Full AI health report</li>
                <li><Check size={16} /> Priority fix list</li>
                <li><Check size={16} /> No hidden fees</li>
              </ul>
              <button 
                className={styles.buyBtn} 
                onClick={() => handlePurchase('single')}
                disabled={loadingTier !== null}
              >
                {loadingTier === 'single' ? (
                  <>Redirecting to secure checkout... <Loader2 size={16} className="spinning" /></>
                ) : (
                  <>{profile && profile.credits > 0 ? 'Buy More Credits' : 'Get Report'} <Zap size={16} /></>
                )}
              </button>
            </div>

            {/* Tier 2: Family Pack */}
            <div className={`${styles.card} ${styles.featured} ${loadingTier === 'family' ? styles.loading : ''}`}>
              <div className={styles.badge}>Best Value</div>
              <div className={styles.cardTop}>
                <div className={styles.icon}><ShieldCheck size={24} /></div>
                <h3>Family Pack</h3>
                <div className={styles.price}>
                  <span className={styles.currency}>£</span>
                  <span className={styles.amount}>9.99</span>
                  <span className={styles.period}>one-time</span>
                </div>
              </div>
              <ul className={styles.features}>
                <li><Check size={16} /> 3 PC diagnostic scans</li>
                <li><Check size={16} /> Use on multiple PCs</li>
                <li><Check size={16} /> Re-scan after fixes</li>
                <li><Check size={16} /> Credits never expire</li>
              </ul>
              <button 
                className={styles.buyBtnFeatured} 
                onClick={() => handlePurchase('family')}
                disabled={loadingTier !== null}
              >
                {loadingTier === 'family' ? (
                  <>Redirecting to secure checkout... <Loader2 size={16} className="spinning" /></>
                ) : (
                  <>{profile && profile.credits > 0 ? 'Get More Family Packs' : 'Get Family Pack'} <Zap size={16} /></>
                )}
              </button>
            </div>
          </div>

          <footer className={styles.footer}>
            <p>No subscription. No recurring charges. Pay once, get your report, done.</p>
            <p style={{ color: 'var(--accent)', fontSize: '0.9rem', marginTop: '8px', fontWeight: 600 }}>Refunds are available within 24 hours if credits are unused.</p>
            <div className={styles.paymentIcons}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" />
            </div>
          </footer>
        </div>
      </main>
      <Footer />
    </div>
  );
}
